"use client";

import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { validateFile } from "@/lib/resume";
import { socialProviderConfig } from "@/lib/social-provider-config";
import { api } from "@/lib/trpc/react";

import { ConnectLinkedinButton } from "./connect-linkedin-button";
import { JoinStepBar } from "./join-step-bar";

interface JoinSourcesStepProps {
  returnUrl?: string;
}

interface ResumeFile {
  name: string;
  size: number;
}

export function JoinSourcesStep({ returnUrl }: JoinSourcesStepProps) {
  const tErr = useTranslations("Join.errors");
  const t = useTranslations("Join.sources");
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [resume, setResume] = useState<ResumeFile | null>(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);

  const getUploadUrl = api.expertProfile.getResumeUploadUrl.useMutation();
  const processResume = api.expertProfile.processResume.useMutation();
  const skipSources = api.expertProfile.skipSources.useMutation();
  const { data: linkedinConnected } =
    api.expertProfile.linkedinConnected.useQuery();

  const uploadFile = useCallback(
    async (file: File) => {
      const problem = validateFile(file);
      if (problem) {
        toast.error(tErr("badFile"), {
          description: tErr(
            problem === "wrong-type" ? "fileWrongType" : "fileTooLarge",
          ),
        });
        return;
      }
      setUploading(true);
      try {
        const { uploadUrl, s3Key } = await getUploadUrl.mutateAsync({
          fileName: file.name,
          contentType: file.type,
        });
        // eslint-disable-next-line no-restricted-globals -- direct S3 presigned URL upload
        const res = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!res.ok) {
          throw new Error("S3 upload failed");
        }
        setResume({ name: file.name, size: file.size });
        setUploadedKey(s3Key);
      } catch (err) {
        toast.error(tErr("uploadFailed"), {
          description: err instanceof Error ? err.message : tErr("tryAgain"),
        });
      } finally {
        setUploading(false);
      }
    },
    [getUploadUrl],
  );

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) {
      void uploadFile(f);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      void uploadFile(f);
    }
    e.target.value = "";
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResume(null);
    setUploadedKey(null);
  };

  const handleContinue = useCallback(async () => {
    try {
      if (resume && uploadedKey) {
        await processResume.mutateAsync({
          s3Key: uploadedKey,
          fileName: resume.name,
        });
      } else {
        await skipSources.mutateAsync();
      }
    } catch (err) {
      toast.error(tErr("continueFailed"), {
        description: err instanceof Error ? err.message : tErr("tryAgain"),
      });
      return;
    }
    const target = returnUrl
      ? `/join?returnUrl=${encodeURIComponent(returnUrl)}`
      : "/join";
    router.push(target);
    router.refresh();
  }, [resume, uploadedKey, processResume, skipSources, returnUrl, router]);

  return (
    <div className="bg-background relative flex min-h-[100dvh] flex-col items-stretch justify-start px-5 pt-14 pb-10 sm:min-h-screen sm:items-center sm:justify-center sm:px-10 sm:py-16">
      {/* No general skip here: the only mandatory step (phone) is already
          behind the user, and the link below defers the resume and advances to
          the profile step rather than exiting onboarding. The exit affordance
          lives on the profile step (JoinSkipButton). */}
      <JoinStepBar active="sources" />

      <div className="mx-auto flex w-full max-w-[520px] flex-1 flex-col gap-6 sm:flex-none sm:gap-7">
        <div>
          <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.025em] sm:text-[28px] sm:leading-[1.15]">
            {t("heading")}
          </h1>
          <p className="mt-2.5 max-w-[470px] text-[14px] leading-[1.55] text-[#606060] sm:text-[15px] dark:text-neutral-400">
            {t("description")}
          </p>
        </div>

        {/* LinkedIn: connected shows the import pill; not connected offers a
            real link path (Google/Microsoft sign-ins have no LinkedIn source
            otherwise and would land on an empty profile). */}
        {linkedinConnected === undefined ? (
          <div className="bg-border h-[18px] w-48 animate-pulse rounded" />
        ) : linkedinConnected ? (
          <div className="flex items-center gap-[11px] px-0.5 text-[13px] text-[#606060] sm:text-[13.5px] dark:text-neutral-400">
            <span className="bg-marketing-blue relative h-[7px] w-[7px] shrink-0 rounded-full shadow-[0_0_0_4px_#dfe6fd]" />
            <span>
              <span className="text-foreground font-medium">
                {t("linkedinConnected")}
              </span>{" "}
              {t("importSoon")}
            </span>
          </div>
        ) : (
          <div className="border-border bg-muted flex items-center justify-between gap-4 rounded-[14px] border-[1.5px] px-4 py-3.5">
            <div className="flex min-w-0 items-center gap-3.5">
              <div className="border-border bg-card flex size-10 shrink-0 items-center justify-center rounded-xl border [&>svg]:!size-5">
                {socialProviderConfig.linkedin.logo}
              </div>
              <div className="min-w-0">
                <p className="text-foreground text-[15px] font-medium">
                  {t("connectLinkedin")}
                </p>
                <p className="truncate text-[12.5px] text-[#606060] dark:text-neutral-400">
                  {t("prefillFromLinkedin")}
                </p>
              </div>
            </div>
            <ConnectLinkedinButton returnUrl={returnUrl} label={t("connect")} />
          </div>
        )}

        {/* Resume drop zone */}
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => {
            setDrag(false);
          }}
          onDrop={handleDrop}
          className={`relative block min-h-[160px] cursor-pointer rounded-[14px] border-[1.5px] border-dashed px-6 pt-6 pb-6 transition-[border-color,background] duration-200 sm:min-h-[188px] sm:px-8 sm:pt-8 sm:pb-7 ${
            drag
              ? "border-marketing-blue dark:border-marketing-blue-light bg-marketing-blue/10"
              : resume
                ? "border-border bg-muted"
                : "border-border bg-transparent"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handleFileChange}
          />

          <div className="mb-3.5 flex items-center justify-between font-mono text-[10px] tracking-[0.14em] text-neutral-500 uppercase sm:mb-[18px] sm:text-[10.5px] dark:text-neutral-400">
            <span>{t("sourceLabel")}</span>
            <span>{t("optional")}</span>
          </div>

          {!resume ? (
            <>
              <div className="text-foreground mb-2 text-[22px] leading-[1.1] font-medium tracking-[-0.025em] sm:mb-2.5 sm:text-[30px] sm:leading-[1.05] sm:tracking-[-0.03em]">
                {t("dropResume")}
              </div>
              <div className="text-[13px] leading-[1.5] text-[#606060] sm:text-[13.5px] sm:leading-[1.55] dark:text-neutral-400">
                <span className="hidden sm:inline">{t("pdfLimitDesktop")}</span>
                <span className="sm:hidden">{t("pdfLimitMobile")}</span>
                {/* Separator is layout, not copy: a catalog value ending in a
                    space is one translator trim away from a broken line. */}
                {" · "}
                <span className="decoration-border text-foreground underline underline-offset-[3px]">
                  <span className="hidden sm:inline">{t("clickToBrowse")}</span>
                  <span className="sm:hidden">{t("tapToChoose")}</span>
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-3 sm:mb-3.5 sm:gap-3.5">
                <span className="bg-marketing-blue inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white sm:h-8 sm:w-8">
                  {uploading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin sm:h-4 sm:w-4" />
                  ) : (
                    <Check
                      className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                      strokeWidth={2.4}
                    />
                  )}
                </span>
                <span className="text-foreground min-w-0 truncate text-[16px] font-medium tracking-[-0.022em] sm:text-[22px]">
                  {resume.name}
                </span>
              </div>
              <div className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.04em] text-[#606060] sm:gap-3 sm:text-[12px] dark:text-neutral-400">
                <span>
                  {t("fileSize", { size: (resume.size / 1024).toFixed(0) })}
                </span>
                <span className="text-neutral-500 dark:text-neutral-400">
                  ·
                </span>
                <span>{t("readyToMerge")}</span>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="ml-auto cursor-pointer font-mono text-[11px] text-neutral-500 underline underline-offset-[3px] sm:text-[11.5px] dark:text-neutral-400"
                >
                  {t("removeResume")}
                </button>
              </div>
            </>
          )}
        </label>

        <div className="flex-1 sm:hidden" />

        <div className="mt-1 flex items-center justify-end gap-4">
          {!resume && (
            <button
              type="button"
              onClick={() => {
                void handleContinue();
              }}
              disabled={uploading || skipSources.isPending}
              className="decoration-border hover:text-foreground cursor-pointer text-[13px] text-[#606060] underline underline-offset-[3px] transition-colors disabled:cursor-not-allowed disabled:opacity-60 dark:text-neutral-400"
            >
              {skipSources.isPending ? t("oneMoment") : t("doLater")}
            </button>
          )}
          {resume && (
            <button
              type="button"
              onClick={() => {
                void handleContinue();
              }}
              disabled={uploading || processResume.isPending}
              className="bg-marketing-blue hover:bg-marketing-blue-dark inline-flex h-[48px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-full px-7 text-[15px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:h-[46px] sm:w-auto"
            >
              {processResume.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("continuing")}
                </>
              ) : (
                <>
                  {t("continue")}
                  <ArrowRight className="h-[15px] w-[15px]" strokeWidth={2.2} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
